import {CreationChannel, CreationProduct, Product} from '@hconnect/apiclient'
import {zodResolver} from '@hookform/resolvers/zod'
import currencyFormatter from 'currency-formatter'
import {useEffect} from 'react'
import {FormProvider, useForm} from 'react-hook-form'

import {useAvailableCountries} from '../../Components/AccountForm/useCountries'
import {InternalErrors} from '../../ErrorMap'
import {useGlobalState} from '../../hooks/useGlobalState'
import {calcDefaultLocale, getBrowserLanguage} from '../../utils'

import {FormStepper} from './components/FormStepper'
import {RequestAccessPayload, useRequestAccess} from './hooks/useRequestAccess'
import {RequestAccessSelfOnboardingFormData} from './RequestAccessSelfOnboarding.types'
import {countryToCurrencyMap} from './utils/currency.utils'
import {RequestAccessSchema} from './validators/RequestAccessSchema'

export const RequestAccessSelfOnboarding = () => {
  const {globalState} = useGlobalState()
  const availableCountries = useAvailableCountries()
  const requestAccessMutation = useRequestAccess()

  const formMethods = useForm<RequestAccessSelfOnboardingFormData>({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: zodResolver(RequestAccessSchema),
    defaultValues: {
      customerIds: [{id: ''}],
      onsiteAccess: false,
      hubAccess: false,
      allProjectsAccess: false,
      fullName: '',
      email: '',
      mobileNumber: '',
      companyName: '',
      market: '',
      countryId: '',
      termsAcceptedVersion: '',
      comment: '',
      deliveryTicketDate: '',
      deliveryTicketNr: '',
      invoiceNr: '',
      invoiceNetTotal: '',
      permissions: undefined
    }
  })

  const mobileNumber = formMethods.watch('mobileNumber')
  const email = formMethods.watch('email')

  useEffect(() => {
    if (
      (mobileNumber || email) &&
      (formMethods.formState.errors.email?.message === InternalErrors.PHONE_OR_EMAIL_REQUIRED ||
        formMethods.formState.errors.mobileNumber?.message ===
          InternalErrors.PHONE_OR_EMAIL_REQUIRED)
    ) {
      formMethods.clearErrors(['mobileNumber', 'email'])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileNumber, email])

  const onSubmit = (data: RequestAccessSelfOnboardingFormData) => {
    const preferredLanguage = !globalState.lng ? getBrowserLanguage() : globalState.lng
    const selectedCountry = availableCountries?.find(
      (country) => country.countryCode === data.countryId
    )
    const defaultLocale = selectedCountry && calcDefaultLocale(selectedCountry, preferredLanguage)

    const formattedPermissions = data.permissions
      ? Object.entries(data.permissions)
          .filter(([, value]) => value)
          .map(([key]) => key)
      : []

    const isRiskyRoleSelected =
      formattedPermissions.includes('canCreateOrders') ||
      formattedPermissions.includes('canChangeOrders')

    if (
      formattedPermissions.includes('canChangeOrders') &&
      !formattedPermissions.includes('canCreateOrders') &&
      !formattedPermissions.includes('canSeeTestsAndCertificates')
    ) {
      formattedPermissions.push('canSeeTestsAndCertificates')
    }

    const body: RequestAccessPayload = {
      fullName: data.fullName || '-',
      email: data.email,
      mobileNumber: data.mobileNumber,
      permissions: formattedPermissions,
      companyName: data.companyName,
      market: data.market,
      countryId: data.countryId,
      customerNrToVerify: data.customerIds[0].id,
      customerNrs: data.customerIds
        .map((customer) => customer.id)
        .filter((id, index) => index !== 0 && !!id),
      isTermsApprovalRequired: globalState.clientId === Product.OnSite,
      creationChannel: 'selfOnboarding' as CreationChannel,
      creationProduct: globalState.clientId as CreationProduct,
      defaultLocale: defaultLocale,
      termsAcceptedOn: new Date().toISOString(),
      termsAcceptedVersion: data.termsAcceptedVersion,
      invoiceNr: isRiskyRoleSelected ? undefined : data.invoiceNr,
      invoiceTotalNetAmount: data.invoiceNetTotal
        ? currencyFormatter
            .unformat(data.invoiceNetTotal, {
              code: countryToCurrencyMap[data.countryId] || 'USD'
            })
            .toString()
        : '0.0',
      deliveryNr: isRiskyRoleSelected ? undefined : data.deliveryTicketNr,
      deliveryDate: data.deliveryTicketDate || undefined,
      allProjectsAccess: data.allProjectsAccess,
      comment: data.comment
    }

    requestAccessMutation.mutate(body)
  }

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => onSubmit(data))
  }

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(onSubmit)}
        data-test-id="request-access-self-onboarding-form"
        style={{height: '100%'}}
      >
        <FormStepper onSubmit={handleSubmit} isLoading={requestAccessMutation.isLoading} />
      </form>
    </FormProvider>
  )
}
